import React, { useMemo } from 'react';
import './SketchViewer.css';

function SketchViewer({ sketchName, geometry, onClose }) {
  const { svgElements, viewBox } = useMemo(() => {
    if (!geometry || !geometry.length) {
      return { svgElements: [], viewBox: '0 0 300 250' };
    }

    // Collect all coordinate points for bounding box
    const points = [];

    geometry.forEach(g => {
      if (g.type === 'LineSegment') {
        points.push([g.start[0], g.start[1]], [g.end[0], g.end[1]]);
      } else if (g.type === 'Circle') {
        const cx = g.center[0], cy = g.center[1], r = g.radius;
        points.push([cx - r, cy - r], [cx + r, cy + r]);
      } else if (g.type === 'ArcOfCircle') {
        const cx = g.center[0], cy = g.center[1], r = g.radius;
        points.push([cx - r, cy - r], [cx + r, cy + r]);
        if (g.start_point) points.push([g.start_point[0], g.start_point[1]]);
        if (g.end_point) points.push([g.end_point[0], g.end_point[1]]);
      } else if (g.type === 'Point') {
        points.push([g.x || g.position?.[0] || 0, g.y || g.position?.[1] || 0]);
      }
    });

    if (points.length === 0) {
      return { svgElements: [], viewBox: '0 0 300 250' };
    }

    // Compute bounding box
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [x, y] of points) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    // Add 10% padding
    const w = maxX - minX || 1;
    const h = maxY - minY || 1;
    const pad = Math.max(w, h) * 0.1;
    minX -= pad; maxX += pad;
    minY -= pad; maxY += pad;

    const bw = maxX - minX;
    const bh = maxY - minY;

    // SVG panel dimensions
    const panelW = 300;
    const panelH = 250;
    const scale = Math.min(panelW / bw, panelH / bh);

    // Transform: FreeCAD Y up -> SVG Y down (flip Y), then scale and center
    const toSvg = (x, y) => {
      const sx = (x - minX) * scale + (panelW - bw * scale) / 2;
      const sy = (maxY - y) * scale + (panelH - bh * scale) / 2; // flip Y
      return [sx, sy];
    };

    // Build grid lines
    const gridElements = [];
    const gridStep = Math.pow(10, Math.floor(Math.log10(Math.max(bw, bh))));
    const gridStart = [Math.floor(minX / gridStep) * gridStep, Math.floor(minY / gridStep) * gridStep];

    for (let gx = gridStart[0]; gx <= maxX; gx += gridStep) {
      const [sx1, sy1] = toSvg(gx, minY);
      const [sx2, sy2] = toSvg(gx, maxY);
      gridElements.push(
        <line key={`gx-${gx}`} x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="#e0e0e0" strokeWidth="0.5" />
      );
    }
    for (let gy = gridStart[1]; gy <= maxY; gy += gridStep) {
      const [sx1, sy1] = toSvg(minX, gy);
      const [sx2, sy2] = toSvg(maxX, gy);
      gridElements.push(
        <line key={`gy-${gy}`} x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="#e0e0e0" strokeWidth="0.5" />
      );
    }

    // Build geometry elements
    const geoElements = geometry.map((g, i) => {
      const stroke = '#0071e3';
      const sw = 2;

      if (g.type === 'LineSegment') {
        const [x1, y1] = toSvg(g.start[0], g.start[1]);
        const [x2, y2] = toSvg(g.end[0], g.end[1]);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={sw} fill="none" />;
      }

      if (g.type === 'Circle') {
        const [cx, cy] = toSvg(g.center[0], g.center[1]);
        const r = g.radius * scale;
        return <circle key={i} cx={cx} cy={cy} r={r} stroke={stroke} strokeWidth={sw} fill="none" />;
      }

      if (g.type === 'ArcOfCircle') {
        const r = g.radius * scale;
        const [sx, sy] = toSvg(g.start_point[0], g.start_point[1]);
        const [ex, ey] = toSvg(g.end_point[0], g.end_point[1]);
        // Determine sweep: FreeCAD arcs go CCW, but Y flip inverts sweep
        const largeArc = Math.abs((g.end_angle || 0) - (g.start_angle || 0)) > 180 ? 1 : 0;
        const sweep = 1; // CW in SVG due to Y flip
        return (
          <path key={i} d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} ${sweep} ${ex} ${ey}`}
            stroke={stroke} strokeWidth={sw} fill="none" />
        );
      }

      return null;
    }).filter(Boolean);

    return {
      svgElements: [...gridElements, ...geoElements],
      viewBox: `0 0 ${panelW} ${panelH}`
    };
  }, [geometry]);

  if (!geometry) return null;

  return (
    <div className="sketch-viewer-panel">
      <div className="sketch-viewer-header">
        <span className="sketch-viewer-title">
          <span className="icon">{'\u270F'}</span>
          {sketchName}
        </span>
        <button className="sketch-viewer-close" onClick={onClose} title="Close">&times;</button>
      </div>
      <svg className="sketch-viewer-svg" viewBox={viewBox} xmlns="http://www.w3.org/2000/svg">
        {svgElements}
        {geometry.length === 0 && (
          <text x="150" y="125" textAnchor="middle" fill="#999" fontSize="13">No geometry</text>
        )}
      </svg>
    </div>
  );
}

export default SketchViewer;
